import type { Bot, HandEvaluator, Rng } from '@/engine/ports';
import type { Action, HandState, LegalAction, PlayerId } from '@/engine/types';
import { biasAggressiveFreq, biasVpipFreq, PERSONALITIES } from './personality';
import { decidePostflop } from './postflop/decide';
import { lookupPreflop } from './preflop/lookup';
import type { PersonalityBias, PersonalityName } from './types';

export type GtoServices = {
  rng: Rng;
  evaluator: HandEvaluator;
  /** Monte Carlo trials for postflop equity. Default 2_000 (fast for sim), 10_000 for analysis. */
  trials?: number;
};

function pickLegal(
  legals: ReadonlyArray<LegalAction>,
  type: LegalAction['type'],
): LegalAction | undefined {
  return legals.find((l) => l.type === type);
}

function materialize(legal: LegalAction, playerId: PlayerId): Action {
  switch (legal.type) {
    case 'fold':
      return { type: 'fold', playerId };
    case 'check':
      return { type: 'check', playerId };
    case 'call':
      return { type: 'call', playerId, amount: legal.amount ?? 0 };
    case 'bet':
      return { type: 'bet', playerId, amount: legal.min ?? 0 };
    case 'raise':
      return { type: 'raise', playerId, to: legal.min ?? 0 };
    case 'allin':
      return { type: 'allin', playerId, amount: legal.amount ?? 0 };
  }
}

/**
 * Preflop decision: look up the chart frequency, bias it by personality,
 * use rng to pick between raise (aggressive) / call (passive) / fold.
 */
function preflopDecision(
  state: HandState,
  playerId: PlayerId,
  bias: PersonalityBias,
  rng: Rng,
  legals: ReadonlyArray<LegalAction>,
): Action {
  const lookup = lookupPreflop(state, playerId);
  const aggressiveFreq = biasAggressiveFreq(lookup.aggressiveFreq, bias);
  const vpipFreq = biasVpipFreq(Math.min(1, lookup.aggressiveFreq + 0.15), bias);

  const roll = rng.next();
  const facingBet = legals.some((l) => l.type === 'call' || l.type === 'raise');

  if (roll < aggressiveFreq) {
    const raise = pickLegal(legals, 'raise');
    if (raise && raise.min !== undefined) {
      // Open raise size = 2.5x BB, or 3x currentBet facing limp
      const level = state.level;
      const target = Math.max(raise.min, Math.floor(level.bb * 2.5));
      const capped = raise.max !== undefined ? Math.min(target, raise.max) : target;
      return { type: 'raise', playerId, to: capped };
    }
    const bet = pickLegal(legals, 'bet');
    if (bet && bet.min !== undefined) return materialize(bet, playerId);
  }

  if (roll < vpipFreq) {
    const call = pickLegal(legals, 'call');
    if (call) return materialize(call, playerId);
    const check = pickLegal(legals, 'check');
    if (check) return materialize(check, playerId);
  }

  if (facingBet) {
    const fold = pickLegal(legals, 'fold');
    if (fold) return materialize(fold, playerId);
  }
  const check = pickLegal(legals, 'check');
  if (check) return materialize(check, playerId);
  return materialize(legals[0] as LegalAction, playerId);
}

/**
 * Create a GTO-approximation bot driven by the provided personality.
 * The bot branches on preflop vs postflop: preflop hits the chart lookup
 * with bias; postflop runs Monte Carlo equity + sizing.
 */
export function createGtoBot(
  personality: PersonalityName | PersonalityBias,
  services: GtoServices,
): Bot {
  const bias: PersonalityBias =
    typeof personality === 'string' ? PERSONALITIES[personality] : personality;

  return {
    decide(state, playerId, legals) {
      if (legals.length === 0) {
        throw new Error(`createGtoBot: no legal actions for ${playerId}`);
      }
      if (state.betting.street === 'preflop') {
        return preflopDecision(state, playerId, bias, services.rng, legals);
      }
      return decidePostflop(state, playerId, {
        rng: services.rng,
        evaluator: services.evaluator,
        trials: services.trials ?? 2_000,
        legals,
      });
    },
  };
}
